import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NewsletterScoreboard } from "./NewsletterScoreboard";
import { NewsletterWeekRows } from "./NewsletterWeekRows";
import { NewsletterMatchRows } from "./NewsletterMatchRows";
import { NewsletterPodium } from "./NewsletterPodium";
import { NewsletterCallForPlayers } from "./NewsletterCallForPlayers";
import { NewsletterMonthStrip } from "./NewsletterMonthStrip";
import { NewsletterUpcoming } from "./NewsletterUpcoming";
import { NewsletterPosts } from "./NewsletterPosts";
import { formatIssueRange } from "./newsletterFormat";

/** Every section of an issue snapshot, in reading order. Older issues simply lack the newer blocks. */
export function NewsletterIssueBody({ snapshot, scheduleHref = "/competition" }) {
  const { t, i18n } = useTranslation();

  const labels = useMemo(() => {
    const date = (iso) => formatIssueRange(iso, iso, i18n.language);
    const outcome = (value) => {
      if (value === "win") return t("newsletterWin", "Win");
      if (value === "loss") return t("newsletterLoss", "Loss");
      if (value === "draw") return t("newsletterDraw", "Draw");
      return "";
    };
    return {
      board: {
        sessions: t("newsletterScoreSessions", "Sessions"),
        players: t("newsletterScorePlayers", "Players"),
        wins: t("newsletterScoreWins", "Wins"),
        rally: t("newsletterScoreRally", "Best rally"),
      },
      weeks: {
        title: t("newsletterTraining", "Training"),
        empty: t("newsletterWeekEmpty", "No session this week"),
        noHighlight: t("newsletterWeekQuiet", "Sessions ran, with no measured highlight yet"),
        range: (start, end) => formatIssueRange(start, end, i18n.language),
        unit: (row) => {
          if (row.kind === "kot") return t("newsletterKot", "King of the Table");
          if (row.kind === "hr") return t("newsletterHr", "bpm");
          if (row.is_personal_best || row.kind === "personal_best") {
            return t("newsletterPb", "personal best");
          }
          return t("newsletterRally", "rally");
        },
      },
      league: { title: t("newsletterChampionship", "Championship"), outcome },
      cup: { title: t("newsletterCup", "Cup"), outcome },
      podiums: {
        title: t("newsletterTournaments", "Tournaments"),
        vs: t("newsletterVs", "vs"),
        date,
        outcome: (value) => (value === "draw" ? "" : outcome(value)),
      },
    };
  }, [i18n.language, t]);

  if (!snapshot) return null;
  const posts = snapshot.posts || [];
  const pinned = posts.filter((p) => p.pinned || p.kind === "announcement");
  const news = posts.filter((p) => !(p.pinned || p.kind === "announcement"));
  const matches = snapshot.championship || [];
  const league = matches.filter((m) => m.competition !== "cup");
  const cup = matches.filter((m) => m.competition === "cup");

  return (
    <>
      <NewsletterPosts posts={pinned} title={t("newsletterAnnouncements", "Announcements")} highlight />
      <NewsletterCallForPlayers calls={snapshot.call_for_players} href={scheduleHref} />
      <NewsletterScoreboard scoreboard={snapshot.scoreboard} labels={labels.board} />
      <NewsletterMatchRows matches={league} labels={labels.league} />
      <NewsletterMatchRows matches={cup} labels={labels.cup} />
      <NewsletterPodium tournaments={snapshot.tournaments} labels={labels.podiums} />
      <NewsletterMonthStrip month={snapshot.month} />
      <NewsletterUpcoming upcoming={snapshot.upcoming} />
      <NewsletterPosts posts={news} title={t("newsletterNews", "News and links")} />
      <NewsletterWeekRows weeks={snapshot.weeks} labels={labels.weeks} />
      {snapshot.activity?.length ? (
        <section className="pingflo-section">
          <h2>{t("newsletterActivity", "Who trained")}</h2>
          <ul className="pingflo-activity">
            {snapshot.activity.map((row) => (
              <li key={row.name}>
                <span className="pingflo-name">{row.name}</span>
                <span>
                  {row.sessions} {t("newsletterSessionsShort", "sessions")}
                </span>
                <span>
                  {row.hours} {t("newsletterHours", "h")}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}
